import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import i18n from 'i18n';
import { Namespace } from 'i18next';

const i18nClones: { [locale: string]: typeof i18n } = {};

const I18nOverrideContext = React.createContext<typeof i18n | undefined>(undefined);

/**
 * HOC to override the locale of a component and its descendants, by wrapping it in context provider
 * which provides a i18n clone with the specified locale.
 *
 * @param locale
 * @param Component
 */
export const overrideLocale = <P extends object>(locale: string, Component: React.ComponentType<P>) => {
  const Wrapped = (props: P) => {
    const { pathname } = useLocation();

    const i18nOverride = useMemo(() => {
      if (locale.toLowerCase() !== i18n.language.toLowerCase()) {
        TelemetryProvider.Instance.trackEvent('LocaleOverride', {
          deviceLocale: i18n.language,
          overrideLocale: locale,
          component: Component.displayName || Component.name,
          pathname,
        });

        if (i18nClones[i18n.language]) {
          return i18nClones[i18n.language];
        }

        const clone = i18n.cloneInstance();
        i18nClones[locale] = clone;
        clone.changeLanguage(locale);
        return clone;
      }

      return undefined;
    }, []);

    return (
      <I18nOverrideContext.Provider value={i18nOverride}>
        <Component {...props} />
      </I18nOverrideContext.Provider>
    );
  };

  Wrapped.displayName = Component.displayName;
  return Wrapped;
};

export const useLocalizedStringsForLocale = (locale: string | undefined, ns?: Namespace) => {
  const [i18nOverride, setI18nOverride] = React.useState<typeof i18n | undefined>(undefined);
  const [overrideIsReady, setOverrideIsReady] = React.useState(false);

  React.useEffect(() => {
    const cloneI18nInstance = async (newLocale: string) => {
      if (newLocale.toLowerCase() === i18n.language.toLowerCase()) {
        return undefined;
      }

      if (!i18nClones[newLocale]) {
        const clone = i18n.cloneInstance();
        await clone.changeLanguage(newLocale);
        i18nClones[newLocale] = clone;
      }

      return i18nClones[newLocale];
    };

    setOverrideIsReady(false);
    // a missing locale is never ready
    if (!locale) {
      return;
    }
    cloneI18nInstance(locale).then((clone) => {
      setI18nOverride(clone);
      setOverrideIsReady(true);
    });
  }, [locale]);

  const { t, ready } = useTranslation(ns, { i18n: i18nOverride, useSuspense: false });

  return { t, ready: overrideIsReady && ready };
};

export const useLocalizedStrings = (ns?: Namespace) => {
  const i18nOverride = useContext(I18nOverrideContext); // if i18nOverride is undefined, useTranslation will use the default global i18n instance
  return useTranslation(ns, { i18n: i18nOverride, useSuspense: false });
};
